import React from 'react';
import NumberFlowReact, { useCanAnimate } from '@number-flow/react';

import { Skeleton } from '../../../../components';

export type StatValueProps = {
  value: number;
  prefix?: string;
  suffix?: string;
  /** Stagger the roll-up start so a row of figures animates in sequence. */
  delay?: number;
  className?: string;
  /** While true, show a sized placeholder instead of the figure (its endpoint is loading). */
  loading?: boolean;
  /** Not wired to an API field yet. Render a `?` instead of a number. */
  missing?: boolean;
  /** The backing API request is failing. Render `N/A` instead of a number. */
  error?: boolean;
  /** Real data arrived but the value is undefined (a ratio dividing by zero). Render a `–`. */
  indeterminate?: boolean;
};

const formatValue = (value: number, prefix: string, suffix: string): string =>
  `${prefix}${Math.round(value).toLocaleString('en-US')}${suffix}`;

/**
 * The figure itself — odometer-rolls from 0 to `value` on reveal (and tweens
 * between values on later updates) via NumberFlow, the same animation the Stats
 * dashboard uses. Kept private and behind the `loading` gate so the roll-up starts
 * once the data is in (and replays each time the value is revealed) rather than
 * running unseen under a skeleton.
 *
 * NumberFlow only animates on a value *change*, so the entrance is driven by
 * starting the displayed value at 0 and bumping it to the target after mount;
 * `delay` staggers that bump so a row of figures rolls up in sequence. Under
 * reduced motion the figure starts at the target (no roll, no flash of 0).
 */
const Figure = ({
  value,
  prefix,
  suffix,
  delay,
  className,
}: {
  value: number;
  prefix: string;
  suffix: string;
  delay: number;
  className?: string;
}): React.ReactElement => {
  const canAnimate = useCanAnimate({ respectMotionPreference: true });
  const [display, setDisplay] = React.useState(() => (canAnimate ? 0 : value));
  React.useEffect(() => {
    const id = window.setTimeout(() => setDisplay(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);

  return (
    <NumberFlowReact
      className={className}
      value={display}
      prefix={prefix || undefined}
      suffix={suffix || undefined}
      format={{ maximumFractionDigits: 0 }}
      locales="en-US"
      animated={canAnimate}
    />
  );
};

/**
 * A figure that rolls up from zero on reveal (mirrors the Stats dashboard's
 * `LiveNumber`). A pure numeric/money value, not translatable copy, so it renders
 * as a plain node with `en-US` grouping. While `loading`, a placeholder sized to
 * the eventual figure stands in so the row doesn't shift on reveal.
 */
export const StatValue = ({
  value,
  prefix = '',
  suffix = '',
  delay = 0,
  className,
  loading = false,
  missing = false,
  error = false,
  indeterminate = false,
}: StatValueProps): React.ReactElement => {
  // Placeholder markers (the same convention as the Stats dashboard's LiveNumber),
  // by cause: `?` not wired to an API yet, `N/A` the API is failing, `–` the value is
  // undefined this period (a ratio dividing by zero) despite real data. Shown instead
  // of a fake number so the gap is visible.
  if (missing || error || indeterminate) {
    const marker = missing ? '?' : error ? 'N/A' : '–';
    return <span className={className}>{marker}</span>;
  }
  if (loading) {
    // The figure's text is known (mock data is local), so size the placeholder to
    // its width in `ch` — a real API, which wouldn't know the value yet, would pass
    // a fixed width instead. `ch`/`em` track the surrounding font at each call site.
    const width = `${Math.max(2, formatValue(value, prefix, suffix).length)}ch`;
    return <Skeleton className={className} width={width} height="0.9em" radius={5} />;
  }
  return <Figure value={value} prefix={prefix} suffix={suffix} delay={delay} className={className} />;
};

export default StatValue;
